import { useMutation } from "react-relay";
import type { GraphQLTaggedNode, MutationParameters, SelectorStoreUpdater, UploadableMap } from "relay-runtime";
import type { UseMutationConfig } from "react-relay";
import { AsyncMutationError } from "@utils/graphqlError";

export { AsyncMutationError };

type AsyncMutationConfig<TMutation extends MutationParameters> = Omit<UseMutationConfig<TMutation>, "onCompleted" | "onError"> & {
	/** Optional store updater after commit */
	updater?: SelectorStoreUpdater<TMutation["response"]>;
	/** Optional optimistic response */
	optimisticResponse?: TMutation["rawResponse"];
	/** Optional optimistic store updater */
	optimisticUpdater?: SelectorStoreUpdater<TMutation["response"]>;
	/** Optional uploadables */
	uploadables?: UploadableMap;
};

/**
 * A wrapper around Relay's `useMutation` that returns a Promise-based commit function.
 *
 * Instead of:
 * ```ts
 * const [commit] = useMutation<MyMutation>(mutation);
 * const result = await new Promise<MyMutation$data>((resolve, reject) => {
 *   commit({ variables: { input }, onCompleted: resolve, onError: reject });
 * });
 * ```
 *
 * You can write:
 * ```ts
 * const [commit, isInFlight] = useAsyncMutation<MyMutation>(mutation);
 * const result = await commit({ variables: { input } });
 * ```
 */
export function useAsyncMutation<TMutation extends MutationParameters>(
	mutation: GraphQLTaggedNode
): [(config: AsyncMutationConfig<TMutation>) => Promise<TMutation["response"]>, boolean] {
	const [commitMutation, isMutationInFlight] = useMutation<TMutation>(mutation);

	const commitAsync = (config: AsyncMutationConfig<TMutation>): Promise<TMutation["response"]> => {
		return new Promise<TMutation["response"]>((resolve, reject) => {
			commitMutation({
				...config,
				onCompleted: (response, errors) => {
					if (errors?.length) {
						reject(new AsyncMutationError(errors));
						return;
					}

					resolve(response);
				},
				onError: reject,
			});
		});
	};

	return [commitAsync, isMutationInFlight];
}
